/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BulkSendTemplates - Gestor de Templates de Mensajes
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file BulkSendTemplates.tsx
 * @version 1.0.1
 * @description Componente para crear, editar y gestionar templates reutilizables
 * @created 2025-01-27 - Issue #1 Templates System
 * @updated 2025-01-28 - Apple Aqua Modal Style + BringToFront System
 * 
 * FEATURES:
 * - ➕ Crear templates con variables
 * - ✏️ Editar templates existentes
 * - 🗑️ Eliminar templates con confirmación
 * - 🏷️ Categorías personalizables
 * - 📊 Estadísticas de uso
 * - 🔍 Filtrado por categoría
 * - 📱 Preview en tiempo real
 * - 📤 Botón "Usar en Composer"
 * - ✨ Modales estilo Apple Aqua con z-index dinámico
 */

import React, { useState, useEffect } from 'react';
import { getHatateToken } from '../../helpers/utils';
import { Card, Button, Row, Col, Badge, Form, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt,
  faPlus,
  faEdit,
  faTrash,
  faTimes,
  faCheck,
  faExclamationTriangle,
  faClock,
  faChartLine,
  faFilter,
  faPaperPlane,
  faSave,
  faTag
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useUserContext } from '../../layouts/UserContext';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES & INTERFACES
// ═══════════════════════════════════════════════════════════════════════════

interface Template {
  id: string;
  phoneId: string;
  name: string;
  category: string;
  content: string;
  variables: string[];
  metadata: {
    createdAt: string;
    updatedAt: string;
    usageCount: number;
    lastUsed: string | null;
  };
}

interface Notification {
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
}

interface BulkSendTemplatesProps {
  onUseTemplate?: (template: Template) => void;
}

const API_BASE_URL = 'https://backend.anubisai.net/api/whatsapp/wasp';
const API_KEY = 'anubis-whatsapp-api-key';

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const BulkSendTemplates: React.FC<BulkSendTemplatesProps> = ({ onUseTemplate }) => {
  
  // ───────────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────────
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
const [deletingCategory, setDeletingCategory] = useState<{
  name: string;
  templatesCount?: number;
  templates?: Array<{ id: string; name: string }>;
} | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [notification, setNotification] = useState<Notification | null>(null);
  const [maxTemplates, setMaxTemplates] = useState(20);
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  
  // ✨ NUEVO: Modal z-index system
  const [modalZIndex, setModalZIndex] = useState(10000);
  // ✨ NUEVO: Estado para agregar categoría inline
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
  name: '',
  category: 'marketing',
  content: '',
  newCategoryName: '' // Temporal para el input inline
});
  
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<Template | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);

  const { user } = useUserContext();
  
  // ───────────────────────────────────────────────────────────────────────
  // API BASE URL
  // ───────────────────────────────────────────────────────────────────────
  const API_TOKEN = getHatateToken();
  const API_BASE = API_BASE_URL;
  
  const [phoneId, setPhoneId] = useState<string>(''); 

  // ✨ NUEVO: BringToFront para modales
  const bringModalToFront = () => {
    setModalZIndex(prev => prev + 1);
  };

  useEffect(() => {
    const fetchPhoneId = async () => {
      const phoneId = user._id;
      try {
        if(phoneId) {
          setPhoneId(phoneId);
        } else {
          showNotification('error', '❌ No se pudo obtener el phoneId del bot');
        }
      } catch (error: any) {
        console.error('Error fetching phoneId:', error);
        showNotification('error', '❌ Error al conectar con el servidor');
      }
    };
    
    fetchPhoneId();
  }, [user]);

  useEffect(() => {
  if (phoneId) {
    loadTemplates();
    loadCategories(); // Ya existe, mantener
  }
}, [phoneId]);
   
  useEffect(() => {
  if (phoneId && categories.length === 0) {
    loadCategories();
  }
}, [phoneId, categories.length]);
  
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);
  
  // ───────────────────────────────────────────────────────────────────────
  // API CALLS
  // ───────────────────────────────────────────────────────────────────────
     
  const loadTemplates = async () => {
    try {     
      setLoading(true);
      const response = await axios.get(`${API_BASE}/templates`, {
        params: {
          phoneId
        }, 
        headers: {
          'x-api-key': API_KEY,
          'Authorization': API_TOKEN      
        }
      });
      if (response.data.success) {
        setTemplates(response.data.templates);
        setMaxTemplates(response.data.maxTemplates);
      }
    } catch (error: any) { 
      showNotification('error', 'Error al cargar templates: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const loadCategories = async () => { 
  try {
    const response = await axios.get(`${API_BASE}/templates-categories`, {
      params: {
        phoneId
      },
      headers: {
        'x-api-key': API_KEY,
        'Authorization': API_TOKEN
      }
    });
    
    if (response.data.success) {
      // 🔥 Backend devuelve "template" en vez de "categories"
      const cats = response.data.categories || response.data.template || [];
      
      if (cats.length === 0) {
        setCategories(['marketing', 'ventas', 'soporte']);
      } else {
        setCategories(cats);
      }
    }
  } catch (error: any) {
    console.error('[Templates] Error loading categories:', error);
    setCategories(['marketing', 'ventas', 'soporte']);
  }
};

// 🆕 Helper: Verificar si categoría existe en backend
const checkCategoryExists = async (categoryName: string): Promise<boolean> => {
  try {
    const response = await axios.get(`${API_BASE}/templates-categories`, {
      params: { phoneId },
      headers: {
        'x-api-key': API_KEY,
        'Authorization': API_TOKEN
      }
    });
    
    if (response.data.success) {
      const backendCategories = response.data.categories || [];
      return backendCategories.includes(categoryName);
    }
    return false;
  } catch (error) {
    console.error('Error checking category:', error);
    return false;
  }
};

// 🆕 Helper: Guardar nueva categoría en backend
const saveNewCategory = async (categoryName: string): Promise<void> => {
  try {
    await axios.post(`${API_BASE}/templates-categories`, {
      phoneId,
      category: categoryName
    }, {
      headers: {
        'x-api-key': API_KEY,
        'Authorization': API_TOKEN
      }
    });
    
    console.log(`[TEMPLATES] Nueva categoría "${categoryName}" guardada en backend`);
  } catch (error: any) {
    console.error('Error saving new category:', error);
    // No lanzar error, permitir que el template se cree igual
  }
};
  
  const createTemplate = async () => {
  try {
    console.debug("[TEMPLATES]: category:" + formData.category.trim() + " - name:" + formData.name.trim() + " - content:" + formData.content.trim());
    if (formData.name.trim() === '' || formData.content.trim() === '' || formData.category.trim() === '') {
      showNotification('warning', '⚠️ Nombre y contenido son obligatorios');
      return;
    }
    
    if (!formData.category || formData.category === '') {
      showNotification('warning', '⚠️ Selecciona una categoría');
      return;
    }
    
    const category = formData.category;
    const variableRegex = /\[([^\]]+)\]/g;
    const variables = [...formData.content.matchAll(variableRegex)].map(match => match[1]);
    
    const response = await axios.post(`${API_BASE}/templates`, { 
      phoneId: phoneId,
      name: formData.name,
      category,
      content: formData.content,
      variables: [...new Set(variables)]
    }, {
      headers: {
        'x-api-key': API_KEY,
        'Authorization': API_TOKEN
      }
    });
    
    if (response.data.success) {
      // 🔥 ORDEN CRÍTICO: Cerrar modal ANTES de recargar
      setShowCreateModal(false);
      showNotification('success', '✅ Template creado correctamente');
      
      // 🔥 Recargar datos en orden
      await loadTemplates();
      await loadCategories();
      
      resetForm();
    }
  } catch (error: any) {
    const message = error.response?.data?.error || error.message;
    showNotification('error', '❌ Error: ' + message);
  }
};
  
  const updateTemplate = async () => {
  if (!editingTemplate) return;
  
  try {
    if (!formData.name.trim() || !formData.content.trim()) {
      showNotification('warning', '⚠️ Nombre y contenido son obligatorios');
      return;
    }
    
    if (!formData.category || formData.category === '') {
      showNotification('warning', '⚠️ Selecciona una categoría');
      return;
    }
    
    const category = formData.category;
    const variableRegex = /\[([^\]]+)\]/g;
    const variables = [...formData.content.matchAll(variableRegex)].map(match => match[1]);
    
    const response = await axios.put(`${API_BASE}/templates/${editingTemplate.id}`, {
      name: formData.name,
      category,
      content: formData.content,
      variables: [...new Set(variables)]
    }, {
      headers: {
        'x-api-key': API_KEY,
        'Authorization': API_TOKEN
      }
    });
    
    if (response.data.success) {
      // 🔥 ORDEN CRÍTICO: Cerrar modal ANTES de recargar
      setShowEditModal(false);
      setEditingTemplate(null);
      showNotification('success', '✅ Template actualizado correctamente');
      
      // 🔥 Recargar datos en orden
      await loadTemplates();
      await loadCategories();
      
      resetForm();
    }
  } catch (error: any) {
    const message = error.response?.data?.error || error.message;
    showNotification('error', '❌ Error: ' + message);
  }
};
  
  const deleteTemplate = async () => {
    if (!deletingTemplate) return;
    
    try {
      const response = await axios.delete(`${API_BASE}/templates/${deletingTemplate.id}`, {
        headers: {
          'x-api-key': API_KEY,
          'Authorization': API_TOKEN
        }
      });
      
      if (response.data.success) {
        showNotification('success', '✅ Template eliminado correctamente');
        setShowDeleteModal(false);
        setDeletingTemplate(null);
        loadTemplates();
      }
    } catch (error: any) {
      const message = error.response?.data?.error || error.message;
      showNotification('error', '❌ Error: ' + message);
    }
  };
  
  const trackUsage = async (template: Template) => {
    try {
      await axios.post(`${API_BASE}/templates/${template.id}/use`, {}, {
        headers: {
          'x-api-key': API_KEY,
          'Authorization': API_TOKEN
        }
      });
      loadTemplates();
    } catch (error: any) {
      console.error('Error tracking usage:', error);
    }
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────────
  
  const showNotification = (type: Notification['type'], message: string) => {
    setNotification({ type, message });
  };
  
  const resetForm = () => {
  // 🔥 CRÍTICO: Usar primera categoría disponible o 'marketing' como fallback
  const defaultCategory = (categories && categories.length > 0) 
    ? categories[0] 
    : 'marketing';
  
  setFormData({
    name: '',
    category: defaultCategory,
    content: '',
    newCategoryName: ''
  });
  setIsAddingCategory(false);
};
  
  const handleCreateClick = async () => {
  if (templates.length >= maxTemplates) {
    showNotification('warning', `⚠️ Límite de ${maxTemplates} templates alcanzado`);
    return;
  }
  
  // 🔥 CRÍTICO: Asegurar categorías cargadas ANTES de abrir modal
  if (categories.length === 0) {
    await loadCategories();
  }
  
  resetForm();
  setIsAddingCategory(false);
  setShowCreateModal(true);
  bringModalToFront();
};
  
  const handleEditClick = (template: Template) => {
  setEditingTemplate(template);
  
  // Si la categoría no existe en la lista, agregarla temporalmente
  if (template.category && !(categories || []).includes(template.category)) {
    setCategories(prev => [...(prev || []), template.category]);
  }
  
  setFormData({
    name: template.name,
    category: template.category, // Siempre usar la categoría real
    content: template.content,
    newCategoryName: ''
  });
  
  setIsAddingCategory(false); // Iniciar con Select visible
  setShowEditModal(true);
  bringModalToFront();
};
  
  const handleDeleteClick = (template: Template) => {
    setDeletingTemplate(template);
    setShowDeleteModal(true);
    bringModalToFront();
  };
  
  const handleUseTemplate = (template: Template) => {
    trackUsage(template);
    if (onUseTemplate) {
      onUseTemplate(template);
    }
    showNotification('success', '✅ Template cargado en el composer');
  };
  
  const handlePreviewClick = (template: Template) => {
    setPreviewTemplate(template);
    setShowPreviewModal(true);
    bringModalToFront();
  };

  const handleCategorySelectChange = (value: string) => {
  if (value === '__ADD_NEW__') {
    setIsAddingCategory(true);
    setFormData({ ...formData, newCategoryName: '' });
  } else {
    setFormData({ ...formData, category: value });
    setIsAddingCategory(false);
  }
};

const handleConfirmNewCategory = () => {
  const newCat = formData.newCategoryName.trim();
  
  if (!newCat) {
    showNotification('warning', '⚠️ Ingresa un nombre para la categoría');
    return;
  }
  
  if ((categories || []).includes(newCat)) {
    showNotification('warning', '⚠️ Esta categoría ya existe');
    return;
  }
  
  // Agregar a la lista local
  setCategories(prev => [...(prev || []), newCat]);
  
  // Seleccionar la nueva categoría
  setFormData({ ...formData, category: newCat, newCategoryName: '' });
  setIsAddingCategory(false);
  
  showNotification('success', `✅ Categoría "${newCat}" agregada`);
};

const handleCancelNewCategory = () => {
  setIsAddingCategory(false);
  setFormData({ ...formData, newCategoryName: '', category: 'marketing' });
};

const handleDeleteCategory = async (categoryName: string) => {
  try {
    const response = await axios.delete(`${API_BASE}/templates-categories/${encodeURIComponent(categoryName)}`, {
      params: { phoneId },
      headers: {
        'x-api-key': API_KEY,
        'Authorization': API_TOKEN
      }
    });
    
    if (response.data.success) {
      showNotification('success', `✅ Categoría "${categoryName}" eliminada`);
      setShowDeleteCategoryModal(false);
      setDeletingCategory(null);
      await loadCategories();
    }
  } catch (error: any) {
    if (error.response?.status === 400) {
      // Mostrar modal con detalles de bloqueo
      const data = error.response.data;
      setDeletingCategory({
        name: categoryName,
        templatesCount: data.templatesCount,
        templates: data.templates
      });
      setShowDeleteCategoryModal(true);
      bringModalToFront();
    } else {
      const message = error.response?.data?.error || error.message;
      showNotification('error', '❌ Error al eliminar: ' + message);
    }
  }
};

const handleDeleteCategoryClick = (categoryName: string) => {
  setDeletingCategory({ name: categoryName });
  setShowDeleteCategoryModal(true);
  bringModalToFront();
};
  
  // ───────────────────────────────────────────────────────────────────────
  // COMPUTED
  // ───────────────────────────────────────────────────────────────────────
  
  const filteredTemplates = (selectedCategory === 'all'
    ? templates
    : templates.filter(t => t.category === selectedCategory)
  ).filter(t => t && t.content);
  
  const categoriesWithCount = (categories || []).map(cat => ({
    name: cat,
    count: templates.filter(t => t && t.content && t.category === cat).length
  }));
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: NOTIFICATION
  // ───────────────────────────────────────────────────────────────────────
  
  const renderNotification = () => {
    if (!notification) return null;
    
    const bgColor = {
      success: '#10b981',
      error: '#ef4444',
      warning: '#f59e0b',
      info: '#3b82f6'
    }[notification.type];
    
    return (
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 99999,
        backgroundColor: bgColor,
        color: 'white',
        padding: '16px 20px',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        fontSize: '0.95rem',
        fontWeight: 500,
        animation: 'slideInRight 0.3s ease-out',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        minWidth: '300px'
      }}>
        <span style={{ flex: 1 }}>{notification.message}</span>
        <button
          onClick={() => setNotification(null)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'white',
            fontSize: '1.2rem',
            cursor: 'pointer',
            padding: 0,
            width: '24px',
            height: '24px'
          }}
        >
          ×
        </button>
      </div>
    );
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: HEADER
  // ───────────────────────────────────────────────────────────────────────
  
  const renderHeader = () => (
    <Card style={{
      border: 'none',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      backgroundColor: 'var(--bs-body-bg)',
      marginBottom: '24px'
    }}>
      <Card.Body style={{ padding: '24px' }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h4 style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--bs-body-color)' }}>
              <FontAwesomeIcon icon={faFileAlt} className="me-2" style={{ color: '#28a745' }} />
              Mis Templates
            </h4>
            <p style={{ color: 'var(--bs-secondary)', marginBottom: 0, fontSize: '0.9rem' }}>
              {templates.filter(t => t && t.content).length} de {maxTemplates} templates creados
            </p>
          </div>
          
          <Button
            variant="success"
            onClick={handleCreateClick}
            disabled={templates.length >= maxTemplates}
            style={{ borderRadius: '8px', padding: '10px 24px', fontWeight: 500 }}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Nuevo Template
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: CATEGORY FILTER
  // ───────────────────────────────────────────────────────────────────────
  
  const renderCategoryFilter = () => (
    <Card style={{
      border: 'none',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      backgroundColor: 'var(--bs-body-bg)',
      marginBottom: '24px'
    }}>
      <Card.Body style={{ padding: '20px' }}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <FontAwesomeIcon icon={faFilter} style={{ color: 'var(--bs-secondary)' }} />
          <span style={{ fontWeight: 500, marginRight: '8px' }}>Filtrar:</span>
          
          <Badge
            bg={selectedCategory === 'all' ? 'primary' : 'secondary'}
            onClick={() => setSelectedCategory('all')}
            style={{ 
              cursor: 'pointer', 
              padding: '8px 14px',
              fontSize: '0.85rem',
              fontWeight: 500
            }}
          >
            Todos ({templates.filter(t => t && t.content).length})
          </Badge>
          
          {categoriesWithCount.map(cat => (
  <div key={cat.name} style={{ position: 'relative', display: 'inline-block' }}>
    <Badge
      bg={selectedCategory === cat.name ? 'primary' : 'secondary'}
      onClick={() => setSelectedCategory(cat.name)}
      style={{ 
        cursor: 'pointer',
        padding: '8px 32px 8px 14px', // Espacio para el botón X
        fontSize: '0.85rem',
        fontWeight: 500,
        textTransform: 'capitalize',
        position: 'relative'
      }}
    >
      {cat.name} ({cat.count})
    </Badge>
    
    {/* Botón eliminar categoría */}
    <button
      onClick={(e) => {
        e.stopPropagation();
        handleDeleteCategoryClick(cat.name);
      }}
      style={{
        position: 'absolute',
        right: '6px',
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'transparent',
        border: 'none',
        color: 'white',
        fontSize: '14px',
        cursor: 'pointer',
        padding: '0 4px',
        lineHeight: 1,
        opacity: 0.7,
        transition: 'opacity 0.2s'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.opacity = '1';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = '0.7';
      }}
      title={`Eliminar categoría "${cat.name}"`}
    >
      ×
    </button>
  </div>
))}
        </div>
      </Card.Body>
    </Card>
  );
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: TEMPLATES GRID
  // ───────────────────────────────────────────────────────────────────────
  
  const renderTemplatesGrid = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <Spinner animation="border" variant="primary" />
          <p style={{ marginTop: '16px', color: 'var(--bs-secondary)' }}>
            Cargando templates...
          </p>
        </div>
      );
    }
    
    if (filteredTemplates.length === 0) {
      return (
        <Card style={{
          border: '2px dashed var(--bs-border-color)',
          borderRadius: '12px',
          backgroundColor: 'var(--bs-body-bg)',
          textAlign: 'center',
          padding: '60px 20px'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📝</div>
          <h5 style={{ fontWeight: 600, marginBottom: '8px' }}>
            {selectedCategory === 'all' 
              ? 'No tienes templates creados' 
              : `No hay templates en la categoría "${selectedCategory}"`}
          </h5>
          <p style={{ color: 'var(--bs-secondary)', marginBottom: '20px' }}>
            {selectedCategory === 'all'
              ? 'Crea tu primer template para reutilizar mensajes'
              : 'Prueba con otra categoría o crea un nuevo template'}
          </p>
          {selectedCategory === 'all' && (
            <Button variant="success" onClick={handleCreateClick} style={{ borderRadius: '8px' }}>
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Crear Primer Template
            </Button>
          )}
        </Card>
      );
    }
    
    return (
      <Row className="g-4">
        {filteredTemplates.map(template => (
          <Col key={template.id} md={6} lg={4}>
            <Card style={{
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              backgroundColor: 'var(--bs-body-bg)',
              height: '100%',
              transition: 'all 0.2s',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
            }}>
              <Card.Body style={{ padding: '20px', display: 'flex', flexDirection: 'column' }}>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div style={{ flex: 1 }}>
                    <h6 style={{ fontWeight: 600, marginBottom: '6px', color: 'var(--bs-body-color)' }}>
                      {template.name}
                    </h6>
                    <Badge 
                      bg="info" 
                      style={{ 
                        fontSize: '0.75rem', 
                        padding: '4px 8px',
                        textTransform: 'capitalize'
                      }}
                    >
                      <FontAwesomeIcon icon={faTag} className="me-1" />
                      {template.category}
                    </Badge>
                  </div>
                </div>
                
                <div style={{
                  flex: 1,
                  backgroundColor: '#f8f9fa',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '12px',
                  fontSize: '0.85rem',
                  color: '#495057',
                  maxHeight: '120px',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  {(template.content || '[Sin contenido]').substring(0, 150)}
                  {(template.content || '').length > 150 && '...'}
                  
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '30px',
                    background: 'linear-gradient(transparent, #f8f9fa)'
                  }}></div>
                </div>
                
                <div className="d-flex justify-content-between align-items-center mb-3" style={{ fontSize: '0.8rem', color: 'var(--bs-secondary)' }}>
                  <div>
                    <FontAwesomeIcon icon={faChartLine} className="me-1" />
                    {template.metadata.usageCount} usos
                  </div>
                  {template.variables.length > 0 && (
                    <div>
                      📝 {template.variables.length} variables
                    </div>
                  )}
                </div>
                
                <div className="d-flex gap-2">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => handleUseTemplate(template)}
                    style={{ flex: 1, borderRadius: '6px', fontSize: '0.85rem' }}
                  >
                    <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
                    Usar
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleEditClick(template)}
                    style={{ borderRadius: '6px' }}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDeleteClick(template)}
                    style={{ borderRadius: '6px' }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // ✨ RENDER: APPLE AQUA MODAL (CREATE/EDIT)
  // ═══════════════════════════════════════════════════════════════════════════
  
  const renderFormModal = (isEdit: boolean) => {
    const show = isEdit ? showEditModal : showCreateModal;
    const onHide = () => {
      if (isEdit) {
        setShowEditModal(false);
        setEditingTemplate(null);
      } else {
        setShowCreateModal(false);
      }
      resetForm();
    };
    const onSave = isEdit ? updateTemplate : createTemplate;
    const title = isEdit ? 'Editar Template' : 'Nuevo Template';
    
    if (!show) return null;
    
    return (
      <>
        {/* Backdrop con blur */}
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            zIndex: modalZIndex,
            animation: 'modalBackdropFadeIn 0.3s ease-out'
          }}
          onClick={onHide}
        />
        
        {/* Modal Window */}
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            backgroundColor: 'var(--bs-body-bg)',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            zIndex: modalZIndex + 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            animation: 'modalSlideIn 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
          }}
          onClick={(e) => {
            e.stopPropagation();
            bringModalToFront();
          }}
        >
          {/* Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--bs-border-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'var(--bs-body-bg)'
          }}>
            <h5 style={{ margin: 0, fontWeight: 600, fontSize: '1.25rem' }}>
              <FontAwesomeIcon icon={isEdit ? faEdit : faPlus} className="me-2" style={{ color: '#007bff' }} />
              {title}
            </h5>
            <button
              onClick={onHide}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '1.5rem',
                color: 'var(--bs-secondary)',
                cursor: 'pointer',
                padding: '0',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
                e.currentTarget.style.color = 'var(--bs-body-color)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--bs-secondary)';
              }}
            >
              ×
            </button>
          </div>
          
          {/* Body */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px'
          }}>
            <Form>
              <Form.Group className="mb-3">
  <Form.Label style={{ fontWeight: 500, marginBottom: '8px' }}>Categoría</Form.Label>
  
  {!isAddingCategory ? (
    // Selector normal
    <Form.Select
  value={formData.category}
  onChange={(e) => handleCategorySelectChange(e.target.value)}
  style={{ 
    borderRadius: '10px',
    border: '1.5px solid var(--bs-border-color)',
    padding: '10px 14px',
    fontSize: '0.95rem',
    transition: 'all 0.2s ease'
  }}
>
  {(categories || []).map(cat => (
    <option key={cat} value={cat} style={{ textTransform: 'capitalize' }}>
      {cat}
    </option>
  ))}
  <option value="__ADD_NEW__">✨ Agregar nueva categoría...</option>
</Form.Select>
  ) : (
    // Input inline para nueva categoría
    <div style={{
      background: 'linear-gradient(180deg, #f5f5f7 0%, #e8e8ea 100%)',
      border: '1.5px solid rgba(0,0,0,0.1)',
      borderRadius: '10px',
      padding: '12px',
      animation: 'slideDown 0.2s ease-out'
    }}>
      <Form.Control
        type="text"
        placeholder="Escribe el nombre de la categoría..."
        value={formData.newCategoryName}
        onChange={(e) => setFormData({ ...formData, newCategoryName: e.target.value })}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleConfirmNewCategory();
          }
        }}
        autoFocus
        style={{ 
          borderRadius: '8px',
          border: '2px solid #007AFF',
          padding: '10px 14px',
          fontSize: '0.95rem',
          fontWeight: 500,
          marginBottom: '10px',
          boxShadow: '0 0 0 4px rgba(0, 122, 255, 0.15)',
          transition: 'all 0.2s ease'
        }}
      />
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleCancelNewCategory}
          style={{
            borderRadius: '8px',
            padding: '6px 16px',
            fontWeight: 500,
            fontSize: '0.85rem',
            background: '#8E8E93',
            border: 'none'
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleConfirmNewCategory}
          style={{
            borderRadius: '8px',
            padding: '6px 16px',
            fontWeight: 500,
            fontSize: '0.85rem',
            background: 'linear-gradient(135deg, #007AFF 0%, #0051D5 100%)',
            border: 'none'
          }}
        >
          ✓ Agregar
        </Button>
      </div>
    </div>
  )}
</Form.Group>
              
              {formData.category === 'custom' && (
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 500, marginBottom: '8px' }}>Nombre de Nueva Categoría</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ej: ventas, soporte"
                    value={formData.newCategoryName}
                    onChange={(e) => setFormData({ ...formData, newCategoryName: e.target.value })}
                    style={{ 
                      borderRadius: '10px',
                      border: '1.5px solid var(--bs-border-color)',
                      padding: '10px 14px',
                      fontSize: '0.95rem'
                    }}
                  />
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 500, marginBottom: '8px' }}>Nombre Template</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ej: ventas, soporte"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    style={{ 
                      borderRadius: '10px',
                      border: '1.5px solid var(--bs-border-color)',
                      padding: '10px 14px',
                      fontSize: '0.95rem'
                    }}
                  />
                </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 500, marginBottom: '8px' }}>Contenido del Mensaje</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  placeholder="Escribe tu mensaje aquí... Usa [variable] para campos personalizables"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  style={{ 
                    borderRadius: '10px',
                    border: '1.5px solid var(--bs-border-color)',
                    padding: '12px 14px',
                    fontSize: '0.9rem',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    lineHeight: '1.5'
                  }}
                />
                <Form.Text style={{ color: 'var(--bs-secondary)', fontSize: '0.85rem', marginTop: '6px', display: 'block' }}>
                  💡 Usa corchetes para variables: [nombre], [telefono], [curso], etc.
                </Form.Text>
              </Form.Group>
              
              {formData.content && (
                <Alert 
                  variant="info"
                  style={{ 
                    borderRadius: '10px',
                    border: '1.5px solid rgba(13, 110, 253, 0.2)',
                    backgroundColor: 'rgba(13, 110, 253, 0.05)'
                  }}
                >
                  <strong className="text-primary-dark" style={{ fontSize: '0.9rem' }}>Variables detectadas:</strong>
                  <div className="mt-2">
                    {[...(formData.content || '').matchAll(/\[([^\]]+)\]/g)].map((match, idx) => (
                      <Badge key={idx} bg="primary" className="me-2" style={{ fontSize: '0.8rem', padding: '4px 10px' }}>
                        {match[1]}
                      </Badge>
                    ))}
                    {[...formData.content.matchAll(/\[([^\]]+)\]/g)].length === 0 && (
                      <span style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)' }}>
                        Ninguna (agrega [variable] en el texto)
                      </span>
                    )}
                  </div>
                </Alert>
              )}
            </Form>
          </div>
          
          {/* Footer */}
          <div style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--bs-border-color)',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            backgroundColor: 'var(--bs-body-bg)'
          }}>
            <Button 
              variant="secondary" 
              onClick={onHide}
              style={{ 
                borderRadius: '10px',
                padding: '10px 24px',
                fontWeight: 500,
                border: 'none'
              }}
            >
              <FontAwesomeIcon icon={faTimes} className="me-2" />
              Cancelar
            </Button>
            <Button 
              variant="success" 
              onClick={onSave}
              style={{ 
                borderRadius: '10px',
                padding: '10px 24px',
                fontWeight: 500,
                border: 'none',
                background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
              }}
            >
              <FontAwesomeIcon icon={faSave} className="me-2" />
              {isEdit ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </div>
      </>
    );
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // ✨ RENDER: APPLE AQUA MODAL (DELETE CONFIRMATION)
  // ═══════════════════════════════════════════════════════════════════════════
  
  const renderDeleteModal = () => {
    if (!showDeleteModal) return null;
    
    return (
      <>
        {/* Backdrop con blur */}
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            zIndex: modalZIndex,
            animation: 'modalBackdropFadeIn 0.3s ease-out'
          }}
          onClick={() => setShowDeleteModal(false)}
        />
        
        {/* Modal Window */}
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '480px',
            backgroundColor: 'var(--bs-body-bg)',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            zIndex: modalZIndex + 1,
            overflow: 'hidden',
            animation: 'modalSlideIn 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
          }}
          onClick={(e) => {
            e.stopPropagation();
            bringModalToFront();
          }}
        >
          {/* Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--bs-border-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'var(--bs-body-bg)'
          }}>
            <h5 style={{ margin: 0, fontWeight: 600, fontSize: '1.25rem', color: '--bs-danger' }}>
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              Confirmar Eliminación
            </h5>
            <button
              onClick={() => setShowDeleteModal(false)}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '1.5rem',
                color: 'var(--bs-secondary)',
                cursor: 'pointer',
                padding: '0',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
                e.currentTarget.style.color = 'var(--bs-body-color)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--bs-secondary)';
              }}
            >
              ×
            </button>
          </div>
          
          {/* Body */}
          <div style={{ padding: '24px' }}>
            <p style={{ marginBottom: '16px', fontSize: '1rem', lineHeight: '1.6' }}>
              ¿Estás seguro de eliminar el template <strong>"{deletingTemplate?.name}"</strong>?
            </p>
            <Alert 
              variant="warning" 
              style={{ 
                marginBottom: 0,
                borderRadius: '10px',
                border: '1.5px solid rgba(255, 193, 7, 0.3)',
                backgroundColor: 'rgba(255, 193, 7, 0.1)',
                padding: '12px 16px'
              }}
            >
              <small style={{ fontSize: '0.9rem' }}>
                ⚠️ Esta acción no se puede deshacer.
              </small>
            </Alert>
          </div>
          
          {/* Footer */}
          <div style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--bs-border-color)',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            backgroundColor: 'var(--bs-body-bg)'
          }}>
            <Button 
              variant="secondary" 
              onClick={() => setShowDeleteModal(false)}
              style={{ 
                borderRadius: '10px',
                padding: '10px 24px',
                fontWeight: 500,
                border: 'none'
              }}
            >
              Cancelar
            </Button>
            <Button 
              variant="danger" 
              onClick={deleteTemplate}
              style={{ 
                borderRadius: '10px',
                padding: '10px 24px',
                fontWeight: 500,
                border: 'none',
                background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)'
              }}
            >
              <FontAwesomeIcon icon={faTrash} className="me-2" />
              Eliminar
            </Button>
          </div>
        </div>
      </>
    );
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
// ✨ RENDER: APPLE AQUA MODAL (DELETE CATEGORY CONFIRMATION)
// ═══════════════════════════════════════════════════════════════════════════

const renderDeleteCategoryModal = () => {
  if (!showDeleteCategoryModal || !deletingCategory) return null;
  
  const hasTemplates = deletingCategory.templatesCount && deletingCategory.templatesCount > 0;
  
  return (
    <>
      {/* Backdrop con blur */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          zIndex: modalZIndex,
          animation: 'modalBackdropFadeIn 0.3s ease-out'
        }}
        onClick={() => {
          setShowDeleteCategoryModal(false);
          setDeletingCategory(null);
        }}
      />
      
      {/* Modal Window */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '520px',
          maxHeight: '80vh',
          backgroundColor: 'var(--bs-body-bg)',
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          zIndex: modalZIndex + 1,
          overflow: 'hidden',
          animation: 'modalSlideIn 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => {
          e.stopPropagation();
          bringModalToFront();
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--bs-border-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'var(--bs-body-bg)'
        }}>
          <h5 style={{ 
            margin: 0, 
            fontWeight: 600, 
            fontSize: '1.25rem', 
            color: hasTemplates ? '#dc3545' : 'var(--bs-danger)',
          }}>
            <FontAwesomeIcon 
              icon={hasTemplates ? faExclamationTriangle : faTrash} 
              className="me-2" 
            />
            {hasTemplates ? 'No se puede eliminar' : 'Confirmar eliminación'}
          </h5>
          <button
            onClick={() => {
              setShowDeleteCategoryModal(false);
              setDeletingCategory(null);
            }}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              color: 'var(--bs-secondary)',
              cursor: 'pointer',
              padding: '0',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
              e.currentTarget.style.color = 'var(--bs-body-color)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--bs-secondary)';
            }}
          >
            ×
          </button>
        </div>
        
        {/* Body */}
        <div style={{ 
          padding: '24px',
          overflowY: 'auto',
          flex: 1
        }}>
          {hasTemplates ? (
            // Caso: Hay templates usando la categoría
            <>
              <p style={{ marginBottom: '16px', fontSize: '1rem', lineHeight: '1.6' }}>
                No se puede eliminar la categoría <strong>"{deletingCategory.name}"</strong> porque{' '}
                <strong>{deletingCategory.templatesCount} template{deletingCategory.templatesCount! > 1 ? 's' : ''}</strong>{' '}
                la está{deletingCategory.templatesCount! > 1 ? 'n' : ''} usando.
              </p>
              
              <Alert 
                variant="warning" 
                style={{ 
                  borderRadius: '10px',
                  border: '1.5px solid rgba(255, 193, 7, 0.3)',
                  backgroundColor: 'rgba(255, 193, 7, 0.1)',
                  padding: '16px',
                  marginBottom: '16px'
                }}
              >
                <div style={{ fontSize: '0.9rem', marginBottom: '12px', fontWeight: 650, color: 'var(--bs-warning)' }}>
                  💡 Para eliminar esta categoría primero debes:
                </div>
                <ol style={{ marginBottom: 0, paddingLeft: '20px', fontSize: '0.9rem', color: 'var(--bs-dark)' }}>
                  <li>Editar cada template listado abajo</li>
                  <li>Cambiar su categoría a otra existente</li>
                  <li>Luego podrás eliminar "{deletingCategory.name}"</li>
                </ol>
              </Alert>
              
              {deletingCategory.templates && deletingCategory.templates.length > 0 && (
                <div>
                  <div style={{ 
                    fontSize: '0.85rem', 
                    fontWeight: 600, 
                    textTransform: 'uppercase',
                    color: 'var(--bs-secondary)',
                    letterSpacing: '0.5px',
                    marginBottom: '12px'
                  }}>
                    Templates afectados:
                  </div>
                  <div style={{
                    background: 'var(--bs-gray-100)',
                    borderRadius: '10px',
                    padding: '12px',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    {deletingCategory.templates.map((template, idx) => (
                      <div 
                        key={template.id}
                        style={{
                          padding: '8px 12px',
                          borderBottom: idx < deletingCategory.templates!.length - 1 
                            ? '1px solid var(--bs-border-color)' 
                            : 'none',
                          fontSize: '0.9rem'
                        }}
                      >
                        <FontAwesomeIcon icon={faFileAlt} className="me-2" style={{ color: '#007bff' }} />
                        {template.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            // Caso: No hay templates, confirmación simple
            <>
              <p style={{ marginBottom: '16px', fontSize: '1rem', lineHeight: '1.6' }}>
                ¿Estás seguro de eliminar la categoría <strong>"{deletingCategory.name}"</strong>?
              </p>
              <Alert 
                variant="info" 
                style={{ 
                  marginBottom: 0,
                  borderRadius: '10px',
                  border: '1.5px solid rgba(13, 110, 253, 0.2)',
                  backgroundColor: 'rgba(13, 110, 253, 0.05)',
                  padding: '12px 16px'
                }}
              >
                <small style={{ fontSize: '0.9rem', color: 'var(--bs-danger)' }}>
                  ℹ️ Esta acción no se puede deshacer.
                </small>
              </Alert>
            </>
          )}
        </div>
        
        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid var(--bs-border-color)',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          backgroundColor: 'var(--bs-body-bg)'
        }}>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowDeleteCategoryModal(false);
              setDeletingCategory(null);
            }}
            style={{ 
              borderRadius: '10px',
              padding: '10px 24px',
              fontWeight: 500,
              border: 'none'
            }}
          >
            {hasTemplates ? 'Entendido' : 'Cancelar'}
          </Button>
          
          {!hasTemplates && (
            <Button 
              variant="danger" 
              onClick={() => handleDeleteCategory(deletingCategory.name)}
              style={{ 
                borderRadius: '10px',
                padding: '10px 24px',
                fontWeight: 500,
                border: 'none',
                background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)'
              }}
            >
              <FontAwesomeIcon icon={faTrash} className="me-2" />
              Eliminar
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: MAIN
  // ───────────────────────────────────────────────────────────────────────
  
  if (!phoneId) {
    return (
      <div style={{ 
        padding: '60px', 
        textAlign: 'center',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Spinner animation="border" variant="primary" style={{ marginBottom: '16px' }} />
        <p style={{ color: 'var(--bs-secondary)', fontSize: '0.95rem' }}>
          Conectando con el servidor...
        </p>
      </div>
    );
  }
  
  return (
    <div style={{ padding: '24px', height: '100%', overflow: 'auto' }}>
      {renderNotification()}
      {renderHeader()}
      {renderCategoryFilter()}
      {renderTemplatesGrid()}
      {renderFormModal(false)}
      {renderFormModal(true)}
      {renderDeleteModal()}
      {renderDeleteCategoryModal()} {/* 🆕 */}
      
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        /* ═══════════════════════════════════════════════════════════════════ */
        /* ✨ APPLE AQUA MODAL ANIMATIONS */
        /* ═══════════════════════════════════════════════════════════════════ */
        
        @keyframes modalBackdropFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.9) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1) translateY(0);
          }
        }

        @keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
        
        /* Smooth transitions para inputs */
        input:focus,
        textarea:focus,
        select:focus {
          outline: none;
          border-color: rgba(13, 110, 253, 0.5) !important;
          box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1) !important;
          transition: all 0.2s ease;
        }
        
        /* Scrollbar personalizado para modales */
        *::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        
        *::-webkit-scrollbar-track {
          background: transparent;
        }
        
        *::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 10px;
        }
        
        *::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.3);
        }
      `}</style>
    </div>
  );
};

export default BulkSendTemplates;